/**
 * liveExitQueue — ce que l'élève envoie en quittant le live (la note, ses chips, « quitter », la
 * sortie de l'appel), qui doit arriver même si le réseau manque à ce moment-là.
 *
 * Ces envois partent quand l'écran s'en va : l'élève sort tout de suite (le live ne le retient pas
 * sur un bouton qui tourne), et un trou réseau au même instant les perdait — vu en recette : après
 * une coupure, ni satisfaction en base, ni trace de sortie. Ici, ils entrent dans une file qui vit
 * hors de l'écran, partent un par un et dans l'ordre (la note avant ses chips : le serveur efface
 * les chips à chaque note), et un échec de réseau ou de serveur les garde : ils repartent au retour
 * du réseau, et sinon à intervalles croissants. Un refus du serveur (un 4xx) ne se renvoie pas ; la
 * suite part quand même.
 *
 * La file n'est pas persistée, et ce qu'elle garde expire au bout d'une demi-heure : la note et la
 * sortie se rangent dans le rapport du jour (`getLiveReportForToday`) et la présence à l'heure de
 * l'envoi, qu'un renvoi le lendemain fausserait. Un envoi n'est jamais fait sous un autre compte que
 * celui qui l'a confié (une bascule vers un frère, une déconnexion) : il est alors abandonné.
 * Chaque abandon (refus, expiration, autre compte) part dans Bugsnag.
 *
 * L'élève qui revient dans la même séance (`joinCall`) n'en est plus sorti : sa sortie en attente
 * (`leave_session`, `leave_call`) est retirée, sinon elle partirait à la reprise et le ferait sortir
 * de l'appel côté serveur alors qu'il y est. Sa note et ses chips, elles, restent : il les a données.
 * Une sortie déjà partie ne se rattrape pas : le retour l'**attend** avant son `joinCall`, pour
 * qu'elle n'arrive jamais au serveur après lui (sinon l'élève, dans l'appel, y serait sorti). Sans
 * délai de réponse sur nos requêtes, l'attente est bornée (`LIVE_EXIT_REJOIN_WAIT_MS`) : au-delà, le
 * retour part quand même, et Bugsnag l'apprend.
 *
 * Deux couches, le patron du moteur : un réducteur pur (`liveExitReducer`, testé en rejouant des
 * suites d'événements) et le magasin du module, qui branche l'envoi, les minuteurs et le réseau.
 */
import NetInfo from "@react-native-community/netinfo"

import { liveService } from "../../../data/services/liveService"
import { liveServiceV2 } from "../../../data/services/liveServiceV2"
import { apiCall, type ApiService } from "../../shared/apiCall"
import { isRetryableFailure } from "../../shared/apiFailure"
import { createTelemetry, type EngineTelemetry } from "../../shared/telemetry"

export type LiveExitKind = "satisfaction" | "student_comment" | "leave_session" | "leave_call"

export type LiveExitRequest = {
    id: string
    kind: LiveExitKind
    liveSessionId: number
    data: Record<string, unknown> | null
    /** Le jeton de la session qui l'a confié : envoyé sous un autre, il serait écrit chez un autre. */
    token: string | null
    queuedAt: number
    attempts: number
    /** Retiré pendant son envoi (l'élève est revenu) : un échec ne le renvoie pas. */
    withdrawn?: boolean
}

export type LiveExitState = {
    items: LiveExitRequest[]
    /** L'envoi en vol (toujours la tête de la file). */
    sending: string | null
    /** La tête a échoué sur le réseau ou le serveur : la file attend le retour du réseau. */
    waitingSince: number | null
}

export type LiveExitEvent =
    | { type: "QUEUED"; request: LiveExitRequest }
    | { type: "SEND_STARTED"; id: string }
    | { type: "SENT"; id: string }
    | { type: "FAILED"; id: string; retryable: boolean; now: number }
    | { type: "RETRY" }
    | { type: "EXPIRED"; now: number }
    | { type: "REJOINED"; liveSessionId: number }

/** Ce qui fait sortir l'élève de la séance : sans objet s'il y revient. */
const LEAVE_KINDS: readonly LiveExitKind[] = ["leave_session", "leave_call"]

const isLeaveOf = (item: LiveExitRequest, liveSessionId: number) =>
    item.liveSessionId === liveSessionId && LEAVE_KINDS.includes(item.kind)

/** Ce qui est gardé plus longtemps ne vaut plus pour cette séance. */
export const LIVE_EXIT_MAX_AGE_MS = 30 * 60 * 1000
/** Ce qu'un retour dans la séance attend, au plus, la sortie déjà en vol avant son `joinCall`. */
export const LIVE_EXIT_REJOIN_WAIT_MS = 5_000
/** Les renvois sans signal du réseau : 2 s, 5 s, 15 s, 30 s, puis chaque minute. */
export const LIVE_EXIT_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000, 60_000]

export const initialLiveExitState: LiveExitState = { items: [], sending: null, waitingSince: null }

export function liveExitReducer(state: LiveExitState, event: LiveExitEvent): LiveExitState {
    switch (event.type) {
        case "QUEUED": {
            const { request } = event
            // Le même geste pour la même séance, pas encore parti : la dernière valeur remplace l'autre.
            const same = state.items.findIndex(
                (item) =>
                    item.kind === request.kind &&
                    item.liveSessionId === request.liveSessionId &&
                    item.id !== state.sending
            )
            if (same >= 0) {
                const items = state.items.slice()
                items[same] = { ...items[same], data: request.data, token: request.token }
                return { ...state, items }
            }
            return { ...state, items: [...state.items, request] }
        }
        case "SEND_STARTED":
            if (state.sending != null || state.items[0]?.id !== event.id) return state
            return {
                ...state,
                sending: event.id,
                items: state.items.map((item) =>
                    item.id === event.id ? { ...item, attempts: item.attempts + 1 } : item
                ),
            }
        case "SENT":
            return {
                items: state.items.filter((item) => item.id !== event.id),
                sending: state.sending === event.id ? null : state.sending,
                waitingSince: null,
            }
        case "FAILED": {
            const sending = state.sending === event.id ? null : state.sending
            const withdrawn = state.items.find((item) => item.id === event.id)?.withdrawn === true
            if (!event.retryable || withdrawn)
                return { ...state, sending, items: state.items.filter((item) => item.id !== event.id) }
            return { ...state, sending, waitingSince: state.waitingSince ?? event.now }
        }
        case "RETRY":
            return state.waitingSince == null ? state : { ...state, waitingSince: null }
        case "EXPIRED": {
            const items = state.items.filter(
                (item) => item.id === state.sending || event.now - item.queuedAt <= LIVE_EXIT_MAX_AGE_MS
            )
            if (items.length === state.items.length) return state
            return { ...state, items, waitingSince: items.length === 0 ? null : state.waitingSince }
        }
        case "REJOINED": {
            if (!state.items.some((item) => isLeaveOf(item, event.liveSessionId))) return state
            // Celui en vol ne se rattrape pas ; il ne repartira pas s'il échoue.
            const items = state.items
                .filter((item) => item.id === state.sending || !isLeaveOf(item, event.liveSessionId))
                .map((item) => (isLeaveOf(item, event.liveSessionId) ? { ...item, withdrawn: true } : item))
            return { ...state, items, waitingSince: items.length === 0 ? null : state.waitingSince }
        }
        default:
            return state
    }
}

/** La tête de la file, si elle peut partir maintenant. */
export function nextLiveExit(state: LiveExitState): LiveExitRequest | null {
    if (state.sending != null || state.waitingSince != null) return null
    return state.items[0] ?? null
}

/* ─────────────────────── le magasin du module ─────────────────────── */

type Dispatch = (action: never) => unknown
type GetState = () => unknown

/** Ce qu'un appelant apprend : parti, gardé pour plus tard, ou abandonné. */
export type LiveExitOutcome = "sent" | "queued" | "dropped"

const SERVICES: Record<LiveExitKind, ApiService> = {
    satisfaction: liveService.sendSatisfaction as ApiService,
    student_comment: liveService.sendStudentComment as ApiService,
    leave_session: liveService.leaveLiveSession as ApiService,
    leave_call: liveServiceV2.leaveCallV2 as ApiService,
}

let state = initialLiveExitState
let dispatchRef: Dispatch | null = null
let getStateRef: GetState | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
let unsubscribeNetwork: (() => void) | null = null
let sequence = 0
let telemetryRef: EngineTelemetry | null = null
const waiters = new Map<string, (outcome: LiveExitOutcome) => void>()
/** L'envoi en vol et sa fin, quelle qu'elle soit : un retour dans la séance l'attend. */
let inFlight: { request: LiveExitRequest; landed: Promise<void>; land: () => void } | null = null

function takeOff(request: LiveExitRequest) {
    let land = () => {}
    const landed = new Promise<void>((resolve) => {
        land = resolve
    })
    inFlight = { request, landed, land }
}

function landed(id: string) {
    if (inFlight?.request.id !== id) return
    inFlight.land()
    inFlight = null
}

const telemetry = () => (telemetryRef ??= createTelemetry("live-student.exit", { role: "student" }))

function tokenOf(getState: GetState | null): string | null {
    const root = getState?.() as { data?: { session?: { object?: { token?: string | null } | null } } } | undefined
    return root?.data?.session?.object?.token ?? null
}

function settle(id: string, outcome: LiveExitOutcome) {
    const waiter = waiters.get(id)
    if (waiter == null) return
    waiters.delete(id)
    waiter(outcome)
}

function apply(event: LiveExitEvent) {
    const before = state
    state = liveExitReducer(state, event)
    const phase = (s: LiveExitState) => (s.sending != null ? "sending" : s.waitingSince != null ? "waiting" : "idle")
    if (phase(before) !== phase(state)) {
        telemetry().transition(phase(before), phase(state), event.type, { queued: state.items.length })
    }
}

function stopWaiting() {
    if (retryTimer != null) clearTimeout(retryTimer)
    retryTimer = null
    unsubscribeNetwork?.()
    unsubscribeNetwork = null
}

function retryNow() {
    if (state.waitingSince == null) return
    stopWaiting()
    expire()
    apply({ type: "RETRY" })
    pump()
}

function expire() {
    const now = Date.now()
    const expired = state.items.filter(
        (item) => item.id !== state.sending && now - item.queuedAt > LIVE_EXIT_MAX_AGE_MS
    )
    if (expired.length === 0) return
    apply({ type: "EXPIRED", now })
    expired.forEach((item) => {
        telemetry().error("expired", new Error("live exit request expired"), {
            kind: item.kind,
            attempts: item.attempts,
        })
        settle(item.id, "dropped")
    })
}

/** La file attend : au retour du réseau (NetInfo, et l'événement `online` du navigateur), ou au minuteur. */
function waitForNetwork() {
    const head = state.items[0]
    const delays = LIVE_EXIT_RETRY_DELAYS_MS
    const delay = delays[Math.min(Math.max((head?.attempts ?? 1) - 1, 0), delays.length - 1)]
    if (retryTimer != null) clearTimeout(retryTimer)
    retryTimer = setTimeout(retryNow, delay)
    if (unsubscribeNetwork != null) return
    const offNetInfo = NetInfo.addEventListener((network) => {
        if (network?.isConnected) retryNow()
    })
    const browser = typeof window !== "undefined" && typeof window.addEventListener === "function" ? window : null
    browser?.addEventListener("online", retryNow)
    unsubscribeNetwork = () => {
        offNetInfo?.()
        browser?.removeEventListener("online", retryNow)
    }
}

function pump() {
    const head = nextLiveExit(state)
    if (head == null) {
        if (state.items.length === 0) stopWaiting()
        return
    }
    const dispatch = dispatchRef
    if (dispatch == null) return
    if (head.token !== tokenOf(getStateRef)) {
        // Le compte a changé (un frère, une déconnexion) : envoyé maintenant, il serait écrit chez un autre.
        apply({ type: "FAILED", id: head.id, retryable: false, now: Date.now() })
        telemetry().error("account_changed", new Error("live exit request dropped"), { kind: head.kind })
        settle(head.id, "dropped")
        pump()
        return
    }
    apply({ type: "SEND_STARTED", id: head.id })
    takeOff(head)
    apiCall(dispatch, SERVICES[head.kind], {
        routeParams: { id: head.liveSessionId },
        ...(head.data != null ? { data: head.data } : {}),
    })
        .then(() => {
            landed(head.id)
            apply({ type: "SENT", id: head.id })
            settle(head.id, "sent")
            pump()
        })
        .catch((cause: unknown) => {
            landed(head.id)
            const retryable = isRetryableFailure(cause)
            apply({ type: "FAILED", id: head.id, retryable, now: Date.now() })
            if (!state.items.some((item) => item.id === head.id)) {
                // Un refus, ou une sortie retirée pendant son envoi (l'élève est revenu).
                if (!retryable) telemetry().error("refused", cause, { kind: head.kind })
                settle(head.id, "dropped")
                pump()
                return
            }
            // Gardés : l'élève n'a pas à les attendre. Chacun saura plus tard s'il est parti.
            state.items.forEach((item) => settle(item.id, "queued"))
            waitForNetwork()
        })
}

/**
 * Confie un envoi à la file. La promesse dit ce qu'il est devenu au premier essai : parti, gardé
 * pour la reprise (le réseau manque), ou abandonné (un refus) — l'appelant n'attend jamais le réseau.
 */
export function enqueueLiveExit(
    dispatch: Dispatch,
    getState: GetState,
    request: { kind: LiveExitKind; liveSessionId: number; data?: Record<string, unknown> | null }
): Promise<LiveExitOutcome> {
    dispatchRef = dispatch
    getStateRef = getState
    sequence += 1
    const queued: LiveExitRequest = {
        id: `${request.kind}-${sequence}`,
        kind: request.kind,
        liveSessionId: request.liveSessionId,
        data: request.data ?? null,
        token: tokenOf(getState),
        queuedAt: Date.now(),
        attempts: 0,
    }
    const outcome = new Promise<LiveExitOutcome>((resolve) => waiters.set(queued.id, resolve))
    const before = state.items.length
    apply({ type: "QUEUED", request: queued })
    if (state.items.length === before) {
        // Il a pris la place d'un envoi identique, pas encore parti : c'est lui qui le dira.
        settle(queued.id, "queued")
    } else if (state.waitingSince != null) {
        settle(queued.id, "queued")
    }
    pump()
    return outcome
}

/**
 * L'élève revient dans la séance (`joinCall`) : sa sortie en attente pour elle est retirée, sa note
 * et ses chips restent. La promesse se tient tant qu'une sortie de la séance est en vol (au plus
 * `LIVE_EXIT_REJOIN_WAIT_MS`) : le `joinCall` part après elle.
 */
export function withdrawLiveExitOnRejoin(liveSessionId: number): Promise<void> {
    const withdrawn = state.items.filter((item) => item.id !== state.sending && isLeaveOf(item, liveSessionId))
    apply({ type: "REJOINED", liveSessionId })
    if (withdrawn.length > 0) {
        telemetry().transition("queued", "withdrawn", "REJOINED", { withdrawn: withdrawn.length })
        withdrawn.forEach((item) => settle(item.id, "dropped"))
        pump()
    }
    const flying = inFlight != null && isLeaveOf(inFlight.request, liveSessionId) ? inFlight : null
    if (flying == null) return Promise.resolve()
    const kind = flying.request.kind
    telemetry().transition("sending", "rejoin_waiting", "REJOINED", { kind })
    return new Promise<void>((resolve) => {
        const timer = setTimeout(() => {
            telemetry().error("rejoin_wait", new Error("live exit still in flight at rejoin"), { kind })
            resolve()
        }, LIVE_EXIT_REJOIN_WAIT_MS)
        void flying.landed.then(() => {
            clearTimeout(timer)
            resolve()
        })
    })
}

/** Pour les tests : vide la file, ses minuteurs et ses écoutes. */
export function resetLiveExitQueue(): void {
    stopWaiting()
    state = initialLiveExitState
    dispatchRef = null
    getStateRef = null
    waiters.clear()
    telemetryRef = null
    inFlight = null
}
